import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import DynamicStepper from './FormEntity/DynamicStepper';
import DynamicForm from './FormEntity/DynamicForm';
import StyledTabs from '../../components/@extended/StyledTabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { getUserRole } from '../util';

const SectionStatus = ({ sectionKey, sectionStatus, userAccess, isSelected }) => {
  const theme = useTheme();
  const user = useSelector((state) => state.auth.user);
  const userData = useSelector((state) => state.auth.userData);
  const { trialId } = useParams();
  const userRole = getUserRole(user, trialId, null, userData);
  const status = useMemo(() => {
    const isVerifyAccess = userAccess?.verify?.find((_role) => _role === userRole);
    const isEditAccess = userAccess?.edit?.find((_role) => _role === userRole);
    if (sectionStatus) {
      let section = sectionStatus?.find((section) => section.sectionKey === sectionKey);
      if (section?.status === 'Pending' || section?.status === 'Verification_Pending') {
        if (
          (userRole === 'site_admin' || userRole === 'site_coordinator') &&
          section?.status === 'Verification_Pending'
        ) {
          return <CheckCircleOutlineRoundedIcon sx={{ color: theme.palette.success.light, fontSize: 20, ml: 1 }} />;
        }
        if (section?.subSectionStatuses.length !== 0) {
          let incomplete = section?.subSectionStatuses.filter((subSection) => subSection.status === 'Pending');
          return (
            <Box ml={1}>
              <Typography
                variant="body2"
                color={isSelected ? theme.palette.common.white : theme.palette.grey[600]}
                sx={{
                  border: `1px solid ${isSelected ? theme.palette.common.white : theme.palette.grey[600]}`,
                  borderRadius: '100%',
                  p: 0.2,
                  px: 0.5,
                  alignSelf: 'end',
                }}
              >{`${section?.subSectionStatuses.length - incomplete.length}/${
                section?.subSectionStatuses.length
              }`}</Typography>
            </Box>
          );
        }
      } else {
        return <CheckCircleOutlineRoundedIcon sx={{ color: theme.palette.success.light, fontSize: 20, ml: 1 }} />;
      }
    }
  }, [sectionStatus, isSelected, theme, userRole, sectionKey]);
  return <>{status}</>;
};

const DynamicFormEngine = (props) => {
  const {
    formConfig,
    AddNewField,
    handleSave,
    hideTab,
    handleSaveSignature,
    handleDownloadSignature,
    formAnswers,
    stepStatusData,
    dynamicFormAction,
    userSiteTrialRole,
    onFormChanged,
  } = props;
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter sections based on access
  const filteredSections = useMemo(() => {
    if (!formConfig?.sections) return [];

    return formConfig.sections.filter((tab) => {
      if (formConfig?.access) {
        return (
          formConfig?.access?.edit?.includes(userSiteTrialRole) ||
          formConfig?.access?.verify?.includes(userSiteTrialRole)
        );
      }
      return true;
    });
  }, [formConfig, userSiteTrialRole]);

  const navigateToNextTab = () => {
    if (!formConfig?.sections || filteredSections.length === 0) {
      navigate(-1);
      return;
    }

    // Find the next available section (considering filtered sections)
    const currentSection = formConfig.sections[activeTab];
    const currentFilteredIndex = filteredSections.findIndex((section) => section.key === currentSection?.key);
    
    if (currentFilteredIndex === -1 || currentFilteredIndex >= filteredSections.length - 1) {
      // If we're at the last filtered section or can't find current, navigate back
      navigate(-1);
      return;
    }
    
    // Get the next filtered section
    const nextFilteredSection = filteredSections[currentFilteredIndex + 1];
    
    // Find the original index of the next section
    const nextTabInd = formConfig.sections.findIndex(
      (section) => section.key === nextFilteredSection.key
    );
    
    if (nextTabInd !== -1) {
      const nextSection = formConfig.sections[nextTabInd];
      setActiveTab(nextTabInd);
      
      // Update URL query params
      const params = new URLSearchParams(searchParams);
      params.set('sectionKey', nextSection.key);
      // If section has subTabs, set first subTab as default, otherwise remove subSectionKey
      if (nextSection.subTabs?.[0]?.key) {
        params.set('subSectionKey', nextSection.subTabs[0].key);
      } else {
        params.delete('subSectionKey');
      }
      setSearchParams(params, { replace: true });
      
      // Call onFormChanged callback if provided
      if (onFormChanged) {
        onFormChanged(nextSection.key, nextSection.subTabs?.[0]?.key || null);
      }
      
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
    } else {
      // If no next section found, navigate back
      navigate(-1);
    }
  };

  // Split sections into rows if more than 6
  const shouldSplitTabs = filteredSections.length > 6;
  const firstRowTabs = shouldSplitTabs
    ? filteredSections.slice(0, Math.ceil(filteredSections.length / 2))
    : filteredSections;
  const secondRowTabs = shouldSplitTabs ? filteredSections.slice(Math.ceil(filteredSections.length / 2)) : [];

  // Initialize activeTab from URL query params
  useEffect(() => {
    // Guard: Only proceed if formConfig and sections exist
    if (!formConfig || !formConfig.sections || formConfig.sections.length === 0) {
      return;
    }

    const querySectionKey = searchParams.get('sectionKey');
    const querySubSectionKey = searchParams.get('subSectionKey');
    const currentSection = formConfig.sections[activeTab];

    // Only update if URL params differ from current state
    if (querySectionKey && querySectionKey !== currentSection?.key) {
      const sectionIndex = formConfig.sections.findIndex(
        (section) => section.key === querySectionKey
      );
      // if (sectionIndex !== -1 && sectionIndex !== activeTab) {
      if (sectionIndex !== -1) {
        setActiveTab(sectionIndex);
        // Call onFormChanged callback if provided
        if (onFormChanged) {
          onFormChanged(querySectionKey, querySubSectionKey || null);
        }
      }
    } else if (!querySectionKey && formConfig.sections.length > 0) {
      // Set default to first section if no query param
      const defaultSection = formConfig.sections[0];
      const params = new URLSearchParams(searchParams);
      params.set('sectionKey', defaultSection.key);
      if (defaultSection.subTabs?.[0]?.key) {
        params.set('subSectionKey', defaultSection.subTabs[0].key);
      } else {
        params.delete('subSectionKey');
      }
      setSearchParams(params, { replace: true });
      if (onFormChanged) {
        onFormChanged(defaultSection.key, defaultSection.subTabs?.[0]?.key || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formConfig, searchParams]);

  const handleTabChange = (_, newTab, isSecondRow = false) => {
    const actualTabIndex = isSecondRow ? newTab + firstRowTabs.length : newTab;
    // Find the original index in formConfig.sections
    const originalIndex = formConfig.sections.findIndex(
      (section) => section.key === filteredSections[actualTabIndex].key
    );
    const selectedSection = formConfig.sections[originalIndex];
    
    setActiveTab(originalIndex);
    
    // Update URL query params
    const params = new URLSearchParams(searchParams);
    params.set('sectionKey', selectedSection.key);
    // If section has subTabs, set first subTab as default, otherwise remove subSectionKey
    if (selectedSection.subTabs?.[0]?.key) {
      params.set('subSectionKey', selectedSection.subTabs[0].key);
    } else {
      params.delete('subSectionKey');
    }
    setSearchParams(params, { replace: true });
    
    // Call onFormChanged callback if provided
    if (onFormChanged) {
      onFormChanged(selectedSection.key, selectedSection.subTabs?.[0]?.key || null);
    }
    
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  };

  const getActiveTabForRow = (isSecondRow) => {
    const currentSection = formConfig.sections[activeTab];
    const currentFilteredIndex = filteredSections.findIndex((section) => section.key === currentSection?.key);

    if (currentFilteredIndex === -1) return false;

    if (isSecondRow) {
      return currentFilteredIndex >= firstRowTabs.length ? currentFilteredIndex - firstRowTabs.length : false;
    } else {
      return currentFilteredIndex < firstRowTabs.length ? currentFilteredIndex : false;
    }
  };

  const renderTabRow = (tabs, isSecondRow = false) => {
    const activeTabValue = getActiveTabForRow(isSecondRow);

    return (
      <StyledTabs
        value={activeTabValue}
        onChange={(e, newTab) => handleTabChange(e, newTab, isSecondRow)}
        indicatorColor="primary"
        textColor="primary"
        variant={tabs.length >= 2 ? 'fullWidth' : 'standard'}
        sx={{
          ...(isSecondRow && { mt: 1 }),
          '& .MuiTab-root': {
            fontSize: shouldSplitTabs ? '0.875rem' : 'inherit',
            minHeight: '50px',
            position: 'relative',
            // paddingTop: '24px', // add space for label abbr
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.key}
            label={
              <Box sx={{ width: '100%', position: 'relative', minHeight: 32 }}>
                {tab?.lableAbrivation ? (
                  <>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        // mt: '1.5rem',
                        textAlign: 'center',
                      }}
                    >
                      {tab.label}
                    </Box>
                  </>
                ) : (
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      minHeight: 32,
                      textAlign: 'center',
                    }}
                  >
                    {tab.label}
                  </Box>
                )}
              </Box>
            }
            icon={
              <SectionStatus
                sectionStatus={stepStatusData?.sectionStatuses}
                sectionKey={tab.key}
                userAccess={formConfig?.access}
                isSelected={activeTabValue === tab.key}
              />
            }
            iconPosition="end"
          />
        ))}
      </StyledTabs>
    );
  };

  return (
    <>
      {formConfig && formConfig.sections && (
        <>
          <Box
            style={{
              display: formConfig?.sections?.length <= 1 || hideTab ? 'none' : 'block',
            }}
          >
            {shouldSplitTabs ? (
              <>
                {renderTabRow(firstRowTabs, false)}
                {renderTabRow(secondRowTabs, true)}
              </>
            ) : (
              renderTabRow(filteredSections, false)
            )}
          </Box>

          {formConfig &&
            formConfig.sections &&
            formConfig.sections.map((tab, index) => {
              let subTabStatus = stepStatusData?.sectionStatuses?.find((section) => section.sectionKey === tab.key);
              return (
                <Box key={`${tab.key}child`} role="tabpanel" hidden={activeTab !== index}>
                  {activeTab === index && tab.subTabs && (
                    <DynamicStepper
                      _config={tab.subTabs}
                      sectionKey={tab.key}
                      AddNewField={AddNewField}
                      handleSave={handleSave}
                      navigateToNextTab={navigateToNextTab}
                      handleSaveSignature={handleSaveSignature}
                      handleDownloadSignature={handleDownloadSignature}
                      formAnswers={formAnswers}
                      sectionsLength={formConfig?.sections?.length}
                      sectionIndex={index + 1}
                      subTabStatus={subTabStatus?.subSectionStatuses}
                      dynamicFormAction={dynamicFormAction}
                      userAccess={formConfig?.access}
                      onFormChanged={onFormChanged}
                      {...props}
                    />
                  )}
                  {activeTab === index && (tab.categories || tab.fields) && (
                    <DynamicForm
                      _config={tab}
                      sectionKey={tab.key}
                      stepStatusData={stepStatusData}
                      subSectionKey={''}
                      AddNewField={AddNewField}
                      handleSave={handleSave}
                      navigateToNextTab={navigateToNextTab}
                      handleSaveSignature={handleSaveSignature}
                      handleDownloadSignature={handleDownloadSignature}
                      formAnswers={formAnswers}
                      sectionsLength={formConfig?.sections?.length}
                      sectionIndex={index + 1}
                      dynamicFormAction={dynamicFormAction}
                      {...props}
                    />
                  )}
                </Box>
              );
            })}
        </>
      )}
      {formConfig && (formConfig.categories || formConfig.fields) && (
        <DynamicForm
          _config={formConfig}
          stepStatusData={stepStatusData}
          sectionKey={''}
          subSectionKey={''}
          handleSave={handleSave}
          handleSaveSignature={handleSaveSignature}
          handleDownloadSignature={handleDownloadSignature}
          dynamicFormAction={dynamicFormAction}
          navigateToNextTab={navigateToNextTab}
          {...props}
        />
      )}
    </>
  );
};

export default DynamicFormEngine;
